/**
 * @fileoverview Skill Definitions - スキル定義テーブル（単一真実源）
 */

/**
 * @typedef {Object} SkillDefinition
 * @property {string} skillType - 一意識別子
 * @property {'special-piece'|'board'|'timer'|'moves'} category - カテゴリ
 * @property {boolean} requiresTarget - ターゲティングの要否
 * @property {string} [specialType] - category='special-piece'の場合の特殊タイプ
 * @property {number} [amount] - category='timer'|'moves'の場合の増加量（将来）
 * @property {Object} ui - UI情報
 * @property {string} ui.name - 表示名
 * @property {string} ui.description - 説明文
 * @property {Object} ui.icon - アイコン定義
 * @property {'svg'|'emoji'|'image'} ui.icon.type - アイコンタイプ
 * @property {string} [ui.icon.viewBox] - SVGの場合のviewBox
 * @property {string} [ui.icon.path] - SVGの場合のパス
 * @property {string} [ui.icon.emoji] - 絵文字の場合の文字
 * @property {string} [ui.icon.src] - 画像の場合のURL
 * @property {string} ui.icon.fallback - フォールバック文字
 */

/**
 * スキル定義テーブル
 * 新しいスキルを追加する場合はここに定義を追加
 */
export const SKILL_DEFINITIONS = Object.freeze({
  area5x5: {
    skillType: 'area5x5',
    category: 'special-piece',
    requiresTarget: true,
    specialType: 'skillExplosion',
    ui: {
      name: 'Explosion',
      description: '爆発で範囲削除',
      icon: {
        type: 'svg',
        viewBox: '0 -960 960 960',
        path: 'm480-225 76-75h104v-104l76-76-76-76v-104H556l-76-76-76 76H300v104l-76 76 76 76v104h102l78 75Zm1 196L346-160H160v-186L26-480l134-134v-186h186l135-134 133 134h186v186l134 134-134 134v186H614L481-29Z',
        fallback: '💣',
      },
    },
  },

  shuffle: {
    skillType: 'shuffle',
    category: 'board',
    requiresTarget: false,
    ui: {
      name: 'Shuffle',
      description: '盤面シャッフル',
      icon: {
        type: 'svg',
        viewBox: '0 -960 960 960',
        path: 'M572-139v-81h108L519-380l58-57 163 160v-109h82v247H572Zm-377 0-56-59 543-542H572v-82h250v249h-82v-109L195-139Zm192-379L139-763l57-59 250 246-59 58Z',
        fallback: '🔀',
      },
    },
  },

  // 将来実装: timerBoost, moveBoost など
});

/**
 * スキル定義を取得
 * @param {string} skillType - スキルタイプ
 * @returns {SkillDefinition|null} スキル定義、存在しない場合はnull
 */
export function getSkillDefinition(skillType) {
  return SKILL_DEFINITIONS[skillType] || null;
}

/**
 * 全スキルタイプのリスト取得
 * @returns {string[]} スキルタイプ配列
 */
export function getAllSkillTypes() {
  return Object.keys(SKILL_DEFINITIONS);
}

/**
 * スキル定義の存在チェック
 * @param {string} skillType - スキルタイプ
 * @returns {boolean} 存在するか
 */
export function hasSkillDefinition(skillType) {
  return skillType in SKILL_DEFINITIONS;
}

/**
 * デフォルトスキルタイプを取得（フォールバック用）
 * @returns {string} デフォルトスキルタイプ
 */
export function getDefaultSkillType() {
  const types = getAllSkillTypes();
  return types[0] || 'area5x5'; // 最初の定義、または固定フォールバック
}
